import { API_ENDPOINT } from 'src/config/env.config';
import { baseApi } from './axios';
import {
	BaseAPIResponse,
	GetDrivePositionsOfACompanyAPIResponse,
	GetEligibleDrivesResponse,
	GetOfflineDriveCandidatesResponse,
	GetOfflineDriveRegisteredCandidatesCsvResponse,
	GetOfflineDrivePositionsResponse,
	OfflineDriveCandidateStatus,
	UserFiltersForApis,
	GetOfflineDriveRoomsResponse,
	GetDriveApplicationsForUserResponse,
	addDriveRoomsInput,
} from 'types';

const baseUrl = `${API_ENDPOINT}/offline-drive`;

export type GetMyRegisteredDrivesResponse = {
	error: boolean;
	latestDriveId: string | null;
	drives: Array<{
		driveId: string;
		driveDate?: string;
		driveEndDate?: string;
		location?: string;
		isArchived?: boolean;
		driveLabel?: string | null;
		registeredAt?: string;
	}>;
};

export const markAttendance = async ({
	offlineDriveRoom,
	isCompany,
	companyIds,
	driveId,
	userIds,
	status,
	allowReenterFromOutOfDrive,
}: {
	offlineDriveRoom?: string;
	isCompany?: boolean;
	companyIds?: string[];
	driveId: string;
	userIds?: string[];
	status: OfflineDriveCandidateStatus;
	allowReenterFromOutOfDrive?: boolean;
}): Promise<GetOfflineDriveCandidatesResponse> => {
	const response = await baseApi.post(`${baseUrl}/markAttendance`, {
		driveId,
		userIds,
		status,
		companyIds,
		isCompany,
		offlineDriveRoom,
		allowReenterFromOutOfDrive,
	});
	return response.data;
};
export const offlineDriveRejectRound = async ({
	userIds,
	positionId,
	remarks,
}: {
	userIds: string[];
	positionId: string;
	remarks: string;
}): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/offlineDriveRejectRound`, { userIds, positionId, remarks });
	return response.data;
};

export const pushUserInPosition = async ({
	driveId,
	positionId,
	userIds,
	roundType,
}: {
	driveId: string;
	positionId: string;
	userIds: string[];
	roundType: string;
}): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/pushUserInPosition`, { positionId, userIds, roundType, driveId });
	return response.data;
};

export const undoOfflineDriveLastRound = async ({
	userId,
	positionId,
	applicationRoundId,
}: {
	userId: string;
	positionId: string;
	applicationRoundId: string;
}): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/undoOfflineDriveLastRound`, {
		userId,
		positionId,
		applicationRoundId,
	});
	return response.data;
};

export const getEligibleDrives = async (driveId?: string): Promise<GetEligibleDrivesResponse> => {
	const params = driveId ? { driveId } : {};
	const response = await baseApi.get(`${baseUrl}/getEligibleDrives`, { params });
	return response.data;
};

export const getMyRegisteredDrives = async (): Promise<GetMyRegisteredDrivesResponse> => {
	const response = await baseApi.get(`${baseUrl}/my-registered-drives`);
	return response.data;
};

export const getOfflineDriveRooms = async (): Promise<GetOfflineDriveRoomsResponse> => {
	const response = await baseApi.get(`${API_ENDPOINT}/offlineDriveRoom/all`);
	return response.data;
};

export const getDrivePositionsOfACompany = async ({
	driveId,
	companyId,
}: {
	driveId: string;
	companyId: string;
}): Promise<GetDrivePositionsOfACompanyAPIResponse> => {
	const response = await baseApi.get(
		`${baseUrl}/getDrivePositionsOfACompany?companyId=${companyId}&driveId=${driveId}`,
	);
	return response.data;
};

export const getOfflineDriveCandidates = async ({
	positionId,
	page,
	limit,
	filters,
	driveId,
}: {
	driveId: string;
	positionId?: string;
	page: number;
	limit?: number;
	filters?: UserFiltersForApis;
}): Promise<GetOfflineDriveCandidatesResponse> => {
	const response = await baseApi.post(`${baseUrl}/getOfflineDriveCandidates`, {
		driveId,
		positionId,
		page,
		limit,
		...filters,
	});
	return response.data;
};
export const getOfflineDriveParticipateCandidates = async ({
	positionId,
	filters,
	driveId,
}: {
	driveId: string;
	positionId?: string;
	filters?: UserFiltersForApis;
}): Promise<GetOfflineDriveRegisteredCandidatesCsvResponse> => {
	const response = await baseApi.post(`${baseUrl}/getOfflineDriveParticipateCandidates`, {
		driveId,
		positionId,
		...filters,
	});
	return response.data;
};

export const getOfflineDrivePositions = async ({
	driveId,
	positionId,
	page,
	limit,
	filters,
}: {
	driveId: string;
	positionId?: string;
	page: number;
	limit?: number;
	filters?: UserFiltersForApis;
}): Promise<GetOfflineDrivePositionsResponse> => {
	const response = await baseApi.post(`${baseUrl}/getOfflineDrivePositions`, {
		driveId,
		positionId,
		page,
		limit,
		...filters,
	});
	return response.data;
};

export const getDriveApplicationsForUser = async ({
	driveId,
}: {
	driveId: string;
}): Promise<GetDriveApplicationsForUserResponse> => {
	const response = await baseApi.get(`${baseUrl}/getDriveApplicationsForUser/${driveId}`);
	return response.data;
};

export const verifyOfflineDriveCandidateDocuments = async ({
	driveId,
	documents,
	candidateIds,
}: {
	driveId: string;
	documents: string[];
	candidateIds: string[];
}): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/verifyOfflineDriveCandidateDocuments`, { candidateIds, driveId, documents });
	return response.data;
};

export const addDriveRooms = async (data: addDriveRoomsInput[]): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`offlineDriveRoom/addDriveRooms`, data);
	return response.data;
}

export const softDeleteDriveRoom = async (id: string): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${API_ENDPOINT}/offlineDriveRoom/softDelete/${id}`);
	return response.data;
}
export const registerForDrive = async (data: { user: string; driveId: string; remarks: string; driveLabel?: string }): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${baseUrl}/registerForDrive`, data);
	return response.data;
}
